// Property creation wizard. Four steps: Title deed (optional, OCR-prefill) →
// Basics → Units → Done. Persists incrementally — the property row is
// created at the end of the Basics step, then each unit is POSTed against
// that propertyId in the Units step. Title deeds and external owners are
// scoped to individual units now and managed inline per unit through the
// shared UnitDocumentsPanel / UnitExternalOwnersPanel components, the same
// ones rendered on the property page. Cancel/Back leaves the half-set-up
// property in place; the user can finish or delete it later through
// PropertyView.
//
// State is kept in instance fields (not full setState) when an input would
// lose focus on every keystroke; only structural changes (step transition,
// unit added/removed) trigger a re-render.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon, unitIcon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { showFormError, clearFormError, fieldFromApiError } from '../core/formError.js';
import { stepper } from '../components/Stepper.js';
import { filePickerHtml, wireDropZones } from '../core/filePicker.js';
import { attachDocumentsPanel } from '../components/DocumentsPanel.js';
import { attachUnitExternalOwnersPanel } from '../components/UnitExternalOwnersPanel.js';
import {
  energySectionHtml, amenitiesSectionHtml, furnishedRadioHtml, countryFieldHtml,
  wireEnergyRatingInputs, wireAmenitiesChips, wireEnergyCertVerify, wireAddressFields, wireFurnishedRadio,
  addressPasteFieldHtml,
} from '../components/PropertyBasicsFields.js';
import { attachUnitMetersPanel } from '../components/UnitMetersPanel.js';
import {
  WIZARD_KIND, loadDraft, saveDraftNow, attachDraftEntity, completeDraft,
} from '../core/wizardDraft.js';
import { stageWizardFile, listWizardFiles, setWizardFileExtractedFields, deleteWizardFile } from '../core/wizardFile.js';
import { UNIT_TYPES as SHARED_UNIT_TYPES, isResidentialType } from '../components/UnitCard.js';
import { ownershipFormToUnitType, decodeAmenities } from '../core/propertyCatalogs.js';

const UNIT_TYPES = SHARED_UNIT_TYPES;
// Wizard flow: TITLE_DEED (optional OCR pre-fill) → BASICS (property
// address + first unit's basics inline, structured by section-head
// sub-sections) → METERS (set up meters on the first unit) → DONE.
// Extra units, address overrides, notes and external owners all live
// on the property-detail page.
const STEP_KEYS = ['title_deed', 'basics', 'rental', 'energy', 'details', 'owners', 'issuer', 'meters', 'done'];
const KIND = WIZARD_KIND.PROPERTY_CREATE;

// Entry modes. The user picks one on a pre-step ("Hogyan szeretnél kezdeni?")
// and that selection drives _stepFlow(). 'deed' is the legacy 8-step flow and
// stays the default for any in-flight draft that predates this picker.
// The 'issuer' step (billing issuer / landlord-of-record designation) only
// renders for agency staff — see _flowKeys(); for self-managed properties
// the creator is the issuer by default and the step would be noise.
const MODES = ['quick', 'deed', 'full'];
const DEFAULT_MODE = 'deed';
const MODE_FLOWS = {
  quick: ['basics', 'issuer', 'meters', 'done'],
  deed:  ['title_deed', 'basics', 'rental', 'energy', 'details', 'owners', 'issuer', 'meters', 'done'],
  full:  ['basics', 'rental', 'energy', 'details', 'owners', 'issuer', 'meters', 'done'],
};

// Agency staff create agency-managed properties (managing_agency_id is
// auto-set server-side), where proformas may not be issued in the staff
// creator's name — they must designate the issuer during setup.
function isAgencyStaff() {
  return (((auth.user || {}).roles) || []).includes('AGENCY_STAFF');
}

export class NewPropertyView extends Component {
  constructor(ctx = null) {
    super();
    // Hash-route may carry a property id + step key for refresh recovery.
    const params = ctx && ctx.params ? ctx.params : {};
    const query = ctx && ctx.query ? ctx.query : {};
    // Resolve the entry mode. URL query wins (deep links), then legacy default
    // when the route is already entity-attached (mid-wizard refresh — we'll
    // overwrite from the draft payload as soon as _hydrate() returns), then
    // null which triggers the mode picker on a fresh /properties/new.
    let mode = MODES.includes(query.mode) ? query.mode : null;
    if (mode === null && params.id) mode = DEFAULT_MODE;
    // Stash the step key from the URL; we can't resolve it to a step number
    // until the mode is known (different modes have different flows). The
    // first render / mount() computes the real step from this key.
    const initialStepKey = params.step || null;
    this.state = {
      step: 1,
      _initialStepKey: initialStepKey,
      mode,
      propertyId: params.id || null,
      property: null,
      _hydrating: false,
      _hydrated: false,
      // Title deed step — captured before the property exists so we can OCR
      // the files via /properties/ocr-preview and prefill Basics/Owners. The
      // wizard accepts multiple files (a tulajdoni lap photographed across
      // several phone snapshots is normal) and ships them to the model in a
      // single call so it can correlate fields across pages.
      titleDeedFiles: [],         // list<File> (PDFs or images)
      titleDeedLoading: false,
      titleDeedError: null,
      ocrFields: null,            // { owners[], address, helyrajzi_szam, ... }
      ocrApplied: false,
      basics: {
        address_line1: '',
        postal_code: '',
        city: '',
        country: 'HU',
        helyrajzi_szam: '',
        currency: 'HUF',
        default_deposit_months: '',
        energy_rating: '',
        energy_certificate_number: '',
        energy_certificate_expires_at: '',
      },
      // The first unit's basics live alongside the property's address on
      // step 1. After basics submit, `unit` is replaced with the seeded
      // unit row (id included) so the Meters step has something to mount
      // its panel against.
      unit: {
        identifier: '1',
        type: 'apartment',
        size_sqm: '',
        room_count: '',
        floor: '',
        helyrajzi_szam: '',
        addr_street: '', addr_house_number: '', addr_building: '',
        addr_staircase: '', addr_door: '',
        postal_code: '', city: '', country: '',
        asking_rent: '',
        kozos_koltseg: '',
        furnished: 'unfurnished',
        max_occupants: '',
        // Energetikai tanúsítvány is per-unit (own wizard step).
        energy_rating: '',
        energy_certificate_number: '',
        energy_certificate_expires_at: '',
        // Felszereltség (amenities) live on the unit too — the property
        // column is just a denormalized cache fed by syncAddressFromMainUnit.
        amenities: [],
      },
      unitSaved: null,
      // Billing-issuer step (agency staff only). null = not loaded yet;
      // _loadIssuer() populates it when the step first mounts.
      issuer: null,
    };
    this._busy = false;
    this._searchTimer = null;
    // Tracks per-unit document panels mounted in the Units step so we
    // can call destroy() on a removed unit's slot before the parent
    // re-renders. Keyed by unit id; each entry holds { docs, owners }.
    this._unitPanels = {};
    // Parallel index of wizard_files row ids for each entry in
    // state.titleDeedFiles. When non-null, the unit-save path uses
    // /units/{id}/documents/from-wizard-file (avoids re-uploading bytes)
    // and lets the entry survive a page refresh as a synthetic
    // File-like row pulled from /wizard-files on hydrate.
    this._titleDeedStagedIds = [];
    // Once we've kicked off a hydrate cycle for this instance we don't
    // run it again on subsequent setState re-renders.
    this._hydratedOnce = false;
    // Resolve the initial step number against the chosen mode's flow now
    // that both are known. When the mode is still null (mode picker is
    // about to show on /properties/new) we leave step at 1 and let the
    // picker handler bump it after the user chooses.
    this.state.step = this._resolveStepFromKey(initialStepKey);
  }

  // Map a step key (from the URL) into a 1-based step number against the
  // current mode's flow. Unknown keys fall back to step 1 — the same
  // behaviour the old STEP_KEYS.indexOf had before mode-aware flows.
  _resolveStepFromKey(stepKey) {
    if (!stepKey || !this.state || !this.state.mode) return 1;
    const flow = this._stepFlow();
    const idx = flow.findIndex((s) => s.key === stepKey);
    return idx >= 0 ? idx + 1 : 1;
  }

  destroy() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._destroyUnitPanels();
    if (this._docsPanel) { try { this._docsPanel.destroy?.(); } catch {} this._docsPanel = null; }
  }

  _destroyUnitPanels(keepIds = null) {
    const ids = Object.keys(this._unitPanels);
    for (const id of ids) {
      if (keepIds && keepIds.has(id)) continue;
      const entry = this._unitPanels[id];
      try { entry?.meters?.destroy?.(); } catch {}
      try { entry?.docs?.destroy?.(); } catch {}
      try { entry?.owners?.destroy?.(); } catch {}
      delete this._unitPanels[id];
    }
  }

  // Step keys for a mode, with the agency-only 'issuer' step filtered out
  // for regular users. Both the live flow and the mode-picker step counts
  // derive from this so they can't drift.
  _flowKeys(mode) {
    const keys = MODE_FLOWS[MODES.includes(mode) ? mode : DEFAULT_MODE];
    return isAgencyStaff() ? keys : keys.filter((k) => k !== 'issuer');
  }

  _stepFlow() {
    const labels = {
      title_deed: t('property.wizard.steps.title_deed'),
      basics:     t('property.wizard.steps.basics'),
      rental:     t('property.wizard.steps.rental') || 'Bérlet',
      energy:     t('property.wizard.steps.energy') || 'Energetika',
      details:    t('property.wizard.steps.details') || 'Felszereltség',
      owners:     t('property.wizard.steps.owners') || 'Tulajdonosok',
      issuer:     t('property.wizard.steps.issuer') || 'Számlakibocsátó',
      meters:     t('property.wizard.steps.meters') || t('property.wizard.steps.units'),
      done:       t('property.wizard.steps.done'),
    };
    const mode = this.state && MODES.includes(this.state.mode) ? this.state.mode : DEFAULT_MODE;
    return this._flowKeys(mode).map((key) => ({ key, label: labels[key] }));
  }

  template() {
    // No mode picked yet (fresh /properties/new with no ?mode= and no
    // entity in the URL) — show the entry-mode picker instead of the
    // normal step UI. Hydration of an entity-attached draft always sets
    // a mode before render, so we never end up here mid-wizard.
    if (this.state.mode === null) return this._modePickerTemplate();
    const flow = this._stepFlow();
    const step = Math.min(Math.max(1, this.state.step), flow.length);
    const isLast = step === flow.length;
    const stepKey = flow[step - 1].key;
    const progressPct = Math.round((step / flow.length) * 100);
    return `
      <section class="stack">
        <a href="#/properties" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('property.wizard.back_cancel'))}</span></a>
        <div data-form-error></div>
        <div class="panel" data-wizard>
          <div class="panel-head">
            <div class="panel-num">${String(step).padStart(2, '0')}</div>
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('property.wizard.eyebrow') || 'Setup · Flow 01')}</div>
              <h2 class="panel-title">${esc(t('property.wizard.title_create') || 'Create')} <span class="panel-title-acc">${esc(t('property.wizard.title_acc') || 'property')}</span></h2>
            </div>
            <div class="panel-status">
              <span class="badge primary"><span class="dot-anim"></span>${esc(t('property.wizard.live') || 'Live')}</span>
            </div>
          </div>
          <div class="panel-body">
            ${stepper(flow.map((s) => ({ key: s.key, label: s.label })), step, { onClickable: true })}
            <div class="wizard-step" data-step-key="${esc(stepKey)}">
              ${this._stepHtml(stepKey)}
            </div>
            ${isLast ? '' : `
              <div class="wizard-nav" style="display:flex; gap:10px; margin-top:24px; padding-top:20px; border-top:1px solid var(--color-border);">
                <button type="button" class="btn btn-ghost" data-action="back" ${step === 1 ? 'disabled' : ''}>
                  ${icon('arrow-left', { size: 'sm' })}<span>${esc(t('property.wizard.nav.back'))}</span>
                </button>
                <span class="grow" style="flex:1;"></span>
                ${['title_deed', 'owners', 'issuer', 'meters'].includes(stepKey) ? `
                  <button type="button" class="btn btn-outline" data-action="skip">
                    <span>${esc(t('property.wizard.nav.skip') || 'Kihagyom')}</span>${icon('arrow-right', { size: 'sm' })}
                  </button>` : ''}
                <button type="button" class="btn btn-primary" data-action="next" ${this._busy ? 'disabled' : ''}>
                  <span data-next-label>${esc(t('property.wizard.nav.next'))}</span>${icon('arrow-right', { size: 'sm' })}
                </button>
              </div>
            `}
          </div>
        </div>
      </section>
    `;
  }

  // Entry-mode picker — shown once on /properties/new before any step UI.
  // Three cards: Gyors (basics + meters), Tulajdoni lap (current 8-step flow
  // with OCR pre-fill), Részletes (manual full flow without the deed step).
  // Picking a card sets state.mode and re-renders into the regular wizard.
  // Reuses the .path-cards / .path-card pattern from the tenancy wizard.
  _modePickerTemplate() {
    const stepsLabel = t('property.wizard.mode_picker.steps_label') || 'lépés';
    const card = (mode, iconName, stepCount) => `
      <button type="button" class="path-card" data-mode-pick="${esc(mode)}">
        <span class="path-card-icon">${icon(iconName)}</span>
        <strong>${esc(t('property.wizard.mode_picker.' + mode + '.title'))}</strong>
        <span class="muted">${esc(t('property.wizard.mode_picker.' + mode + '.desc'))}</span>
        <span class="badge" style="margin-top: var(--space-1);">${esc(stepCount)} ${esc(stepsLabel)}</span>
      </button>`;
    return `
      <section class="stack">
        <a href="#/properties" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('property.wizard.back_cancel'))}</span></a>
        <div class="panel" data-wizard>
          <div class="panel-head">
            <div class="panel-num">00</div>
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('property.wizard.eyebrow') || 'Setup · Flow 01')}</div>
              <h2 class="panel-title">${esc(t('property.wizard.mode_picker.title') || 'Hogyan szeretnél kezdeni?')}</h2>
            </div>
            <div class="panel-status">
              <span class="badge primary"><span class="dot-anim"></span>${esc(t('property.wizard.live') || 'Live')}</span>
            </div>
          </div>
          <div class="panel-body">
            <p class="panel-lede">${esc(t('property.wizard.mode_picker.lede') || 'Válaszd ki, mennyi adatot szeretnél most rögzíteni — bármit később is hozzáadhatsz.')}</p>
            <div class="path-cards">
              ${card('quick', 'zap', this._flowKeys('quick').length - 1)}
              ${card('deed', 'file-text', this._flowKeys('deed').length - 1)}
              ${card('full', 'list-checks', this._flowKeys('full').length - 1)}
            </div>
          </div>
        </div>
      </section>
    `;
  }

  _stepHtml(key) {
    if (key === 'title_deed') return this._stepTitleDeedHtml();
    if (key === 'basics') return this._stepBasicsHtml();
    if (key === 'rental') return this._stepRentalHtml();
    if (key === 'energy') return this._stepEnergyHtml();
    if (key === 'details') return this._stepDetailsHtml();
    if (key === 'owners') return this._stepOwnersHtml();
    if (key === 'issuer') return this._stepIssuerHtml();
    if (key === 'meters') return this._stepMetersHtml();
    if (key === 'done') return this._stepDoneHtml();
    return '';
  }

  // Energetikai tanúsítvány + felszereltség + fotók/dokumentumok. These
  // moved off Alapadatok (which is now just Cím + Első egység) so the
  // first step stays short. Furnished + amenities PUT the first unit on
  // "next" (the property's amenities column is a cache that
  // syncAddressFromMainUnit refreshes); the documents/photos panel
  // uploads immediately via its own API against the property.
  _stepDetailsHtml() {
    const u = this.state.unit || {};
    const propertyId = this.state.propertyId || '';
    // Felszereltség drives public-listing search only, so hide the block
    // (and its section head) when the account can't publish — unless the
    // unit already carries amenities, so we never hide stored data.
    const showAmenities = auth.marketplaceEnabled || decodeAmenities(u.amenities).length > 0;
    const sectionHead = (label, hint = '') => `
      <div class="section-head">
        <div class="section-head-titles">
          <span class="section-head-eyebrow">${esc(label)}</span>
          ${hint ? `<span class="faint" style="text-transform:none; letter-spacing:0; font-size:12.5px;">${esc(hint)}</span>` : ''}
        </div>
      </div>`;
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          <p class="panel-lede">${esc(t('property.wizard.details.help') || 'Kiegészítő adatok és dokumentumok. Mind opcionális — később az ingatlan oldalán is megadhatod.')}</p>

          <form class="stack" data-details-form autocomplete="off">
            ${sectionHead(
              t('property.unit_form.furnished') || 'Bútorozottság',
              t('property.wizard.details.furnished_hint') || 'Az egység bútorozottsága',
            )}
            ${furnishedRadioHtml(u.furnished)}

            ${showAmenities ? `
              ${sectionHead(
                t('property.wizard.basics.section_amenities') || 'Felszereltség',
                t('property.wizard.basics.section_amenities_hint') || 'A bérlők ez alapján szűrnek a kereső oldalon',
              )}
              ${amenitiesSectionHtml(u.amenities)}` : ''}
          </form>

          ${sectionHead(
            t('property.wizard.details.docs_section') || 'Fotók és dokumentumok',
            t('property.wizard.details.docs_hint') || 'Ingatlan fotók, energetikai tanúsítvány, egyéb iratok',
          )}
          ${propertyId
            ? '<div data-documents-slot></div>'
            : `<div class="muted">${esc(t('common.loading') || 'Loading…')}</div>`}
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.basics.note_energy_label') || 'Energetikai tanúsítvány · jogi')}</span>
            ${esc(t('property.wizard.basics.note_energy_help') || 'Minden bérleti hirdetéshez kötelező. A tanúsítvány 10 évig érvényes; a Nestly 60 nappal lejárat előtt szól.')}
          </div>
          <div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.details.note_docs_label') || 'Fotók')}</span>
            ${esc(t('property.wizard.details.note_docs_help') || 'Tölts fel néhány fotót az ingatlanról — ezek a nyilvános hirdetésen és a bérlőknek küldött anyagokban is megjelenhetnek.')}
          </div>
        </aside>
      </div>
    `;
  }

  // Once the listing-core steps (basics → rental → energy → details) are
  // done, the public hirdetés is ready. The remaining steps (owners, meters)
  // are optional management settings, so we lead them with a clear milestone
  // banner — reusing the .note accent + .badge success pattern already on
  // this page — to signal the user can stop here and publish.
  _listingReadyBanner() {
    return `
      <div class="note accent" style="margin-bottom: 18px; display: flex; flex-direction: column; gap: 8px;">
        <span class="badge success" style="align-self: flex-start;"><span class="dot-anim"></span>${esc(t('property.wizard.listing_ready.badge') || 'Hirdetés kész')}</span>
        <span>${esc(t('property.wizard.listing_ready.help') || 'A nyilvános hirdetéshez minden megvan. A tulajdonosok és a mérők opcionális kezelési beállítások — később az ingatlan oldalán is megadhatod.')}</span>
      </div>`;
  }

  _stepOwnersHtml() {
    const u = this.state.unitSaved;
    const propertyId = this.state.propertyId || '';
    if (!u) {
      return `<div class="wizard-cols"><div class="wizard-col-main"><div class="muted">${esc(t('common.loading') || 'Loading…')}</div></div></div>`;
    }
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          ${this._listingReadyBanner()}
          <p class="panel-lede">${esc(t('property.wizard.owners.help') || 'A tulajdoni lapból automatikusan beolvasott tulajdonosok. Ellenőrizd és módosítsd, ha szükséges — ezek nem rendszerfelhasználók, csak hivatkozási céllal kerülnek tárolásra.')}</p>
          <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.owners.for_unit') || 'Tulajdonosok ehhez az egységhez')} · <strong>${esc(u.identifier || '—')}</strong></div>
          <div data-unit-external-owners-slot="${esc(u.id)}"></div>
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.owners.note_label') || 'Miért külön kell rögzíteni?')}</span>
            ${esc(t('property.wizard.owners.note_help') || 'A tulajdonosok a tulajdoni lapból kerülnek be — ezek nem Nestly-fiókkal rendelkező felhasználók, csak nyilvántartási céllal tároljuk őket (pl. szerződésekhez). Külön invitálhatsz Nestly-tagokat az ingatlan oldalán a "Tagok" résznél.')}
          </div>
          ${propertyId ? `<div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.owners.note_skip_label') || 'Kihagyhatod')}</span>
            ${esc(t('property.wizard.owners.note_skip_help') || 'Ha most nincs tulajdoni lap a kezedben, ugorj a következő lépésre — később az ingatlan oldaláról bármikor hozzáadhatod.')}
          </div>` : ''}
        </aside>
      </div>
    `;
  }

  // Billing issuer / landlord-of-record designation (agency staff only).
  // Radio list of eligible member users (OWNER/LANDLORD role) + external
  // deed owners recorded in the Owners step; picking one PUTs
  // /properties/{id}/landlord immediately (same semantics as the panels on
  // the property page). An invite-by-email form covers the "owner should
  // get an account" case — the membership lands as a pending invitation.
  _stepIssuerHtml() {
    const propertyId = this.state.propertyId || '';
    const iss = this.state.issuer;
    if (!propertyId || !iss || iss.loading) {
      return `<div class="wizard-cols"><div class="wizard-col-main"><div class="muted">${esc(t('common.loading') || 'Loading…')}</div></div></div>`;
    }
    const selUser = iss.landlordUserId || null;
    const selExt = iss.landlordExternalOwnerId || null;
    const eligibleMembers = (iss.members || []).filter((m) => {
      const role = String(m.role || '').toUpperCase();
      return role === 'OWNER' || role === 'LANDLORD';
    });
    const memberRow = (m) => `
      <label class="list-item" style="cursor:pointer;">
        <input type="radio" name="wizard-issuer" data-issuer-user="${esc(m.user_id)}"
          ${selUser && String(selUser) === String(m.user_id) ? 'checked' : ''} />
        <div class="stack-2 grow">
          <strong>${esc(m.full_name || m.email || '—')}</strong>
          <span class="faint">${esc([m.email, t('members.roles.' + String(m.role || '').toUpperCase())].filter(Boolean).join(' · '))}</span>
        </div>
      </label>`;
    const ownerRow = (o) => {
      const meta = [
        o.share || '',
        o.unit_identifier ? `${t('property.wizard.issuer.unit_label')} ${o.unit_identifier}` : '',
        o.bank_account ? '' : t('property.wizard.issuer.no_bank'),
      ].filter(Boolean).join(' · ');
      return `
      <label class="list-item" style="cursor:pointer;">
        <input type="radio" name="wizard-issuer" data-issuer-ext="${esc(o.id)}"
          ${selExt && String(selExt) === String(o.id) ? 'checked' : ''} />
        <div class="stack-2 grow">
          <strong>${esc(o.name || '—')}</strong>
          <span class="faint">${esc(meta)}</span>
        </div>
      </label>`;
    };
    const hasCandidates = eligibleMembers.length > 0 || (iss.owners || []).length > 0;
    return `
      <div class="wizard-cols" data-issuer-step>
        <div class="wizard-col-main">
          <p class="panel-lede">${esc(t('property.wizard.issuer.help'))}</p>
          ${iss.error ? `<div class="error-banner">${esc(iss.error)}</div>` : ''}
          ${hasCandidates ? `
            ${eligibleMembers.length ? `
              <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.issuer.members_title'))}</div>
              <div class="list">${eligibleMembers.map(memberRow).join('')}</div>` : ''}
            ${(iss.owners || []).length ? `
              <div class="eyebrow" style="margin: 16px 0 12px;">${esc(t('property.wizard.issuer.owners_title'))}</div>
              <div class="list">${(iss.owners || []).map(ownerRow).join('')}</div>` : ''}
          ` : `
            <div class="empty" style="background: var(--color-bg-card); border: 1px dashed var(--color-border); border-radius: 8px; padding: 16px; text-align: center; color: var(--color-text-faint);">${esc(t('property.wizard.issuer.none'))}</div>
          `}
          ${(selUser || selExt) ? `<div class="note accent" style="margin-top: 14px;">${esc(t('property.wizard.issuer.selected_note'))}</div>` : ''}
          <div class="section-head" style="margin-top: 22px;">
            <div class="section-head-titles">
              <span class="section-head-eyebrow">${esc(t('property.wizard.issuer.invite_title'))}</span>
            </div>
          </div>
          ${(iss.pending || []).map((p) => `<div class="note" style="margin-bottom: 10px;">${esc(t('property.wizard.issuer.invite_pending', { email: p.email || '' }))}</div>`).join('')}
          ${iss.inviteNotice ? `<div class="note accent" style="margin-bottom: 10px;">${esc(iss.inviteNotice)}</div>` : ''}
          ${iss.inviteError ? `<div class="error-banner">${esc(iss.inviteError)}</div>` : ''}
          <div class="row" style="gap: 10px; align-items: center;">
            <input type="email" class="input grow" data-issuer-invite-email
              placeholder="${esc(t('property.wizard.issuer.invite_placeholder'))}" value="${esc(iss.inviteEmail || '')}" />
            <button type="button" class="btn btn-outline" data-action="issuer-invite" ${iss.inviteBusy ? 'disabled' : ''}>
              ${esc(t('property.wizard.issuer.invite_send'))}
            </button>
          </div>
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.issuer.note_label'))}</span>
            ${esc(t('property.wizard.issuer.note_help'))}
          </div>
          <div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.issuer.skip_label'))}</span>
            ${esc(t('property.wizard.issuer.skip_help'))}
          </div>
        </aside>
      </div>
    `;
  }


  _stepBasicsHtml() {
    const b = this.state.basics;
    const cur = esc(b.currency || 'HUF');
    const sectionHead = (label, hint = '') => `
      <div class="section-head">
        <div class="section-head-titles">
          <span class="section-head-eyebrow">${esc(label)}</span>
          ${hint ? `<span class="faint" style="text-transform:none; letter-spacing:0; font-size:12.5px;">${esc(hint)}</span>` : ''}
        </div>
      </div>`;
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          <form class="stack" data-basics-form autocomplete="off">
            <p class="panel-lede">${esc(t('property.wizard.basics.help'))}</p>

            ${this._firstUnitSectionHtml(cur, sectionHead)}

            <span class="faint">${esc(t('property.wizard.basics.settings_hint'))}</span>
          </form>
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.basics.note_unit_label') || 'Első egység')}</span>
            ${esc(t('property.wizard.basics.note_unit_help') || 'Minden ingatlanhoz tartozik legalább egy bérelhető egység. Itt csak az alapadatait add meg — mérőket a következő lépésben, további egységeket az ingatlan oldalán adhatsz hozzá.')}
          </div>
          <div class="note" style="margin-top: 20px;">
            <span class="note-l">${esc(t('property.wizard.basics.note_next_label') || 'Következő')}</span>
            ${esc(t('property.wizard.basics.note_next_help') || 'A következő lépésben jönnek a részletek: energetikai tanúsítvány, felszereltség, fotók és dokumentumok.')}
          </div>
        </aside>
      </div>
    `;
  }

  // Inline first-unit basics. Kept intentionally compact: a single
  // "Első egység" eyebrow + the fields a landlord knows on day one
  // (azonosító, type, méret + szoba + emelet, bér + közös, bútorozottság
  // + max létszám). Address overrides, notes and meters live elsewhere.
  // `sectionHead` is the shared helper from _stepBasicsHtml so all
  // sub-section headings render identically.
  _firstUnitSectionHtml(currency, sectionHead) {
    const u = this.state.unit || {};
    const fields = [
      ['unfurnished',     t('property.unit_form.furnished_unfurnished') || 'Unfurnished'],
      ['partly_furnished', t('property.unit_form.furnished_partly')     || 'Partly furnished'],
      ['fully_furnished',  t('property.unit_form.furnished_fully')      || 'Fully furnished'],
    ];
    const isApt = isResidentialType(u.type || 'apartment');
    const headHtml = sectionHead
      ? sectionHead(
          t('property.wizard.basics.first_unit_eyebrow') || 'Első egység',
          t('property.wizard.basics.first_unit_subtitle') || 'A bérelhető egység alapadatai',
        )
      : '';
    // Identity + the unit's full address. The address belongs to the unit
    // (the property is just the wrapper that groups units and caches this
    // address) — so it's entered here, once. Rental terms (méret, bér,
    // közös, bútorozottság, max) live on the separate "Bérlet" step.
    // `unit_` prefixed names route into state.unit.
    // Type picker: a row of icon tiles instead of a dropdown. Same visual
    // language as the entry-mode picker (.path-card) but compact enough to
    // sit inside the form. The hidden input keeps form-data readers (none
    // currently rely on it — _submitBasics reads state.unit.type — but it's
    // cheap insurance) in sync with the selection.
    const currentType = u.type || 'apartment';
    const typeTilesHtml = `
      <div class="unit-type-tiles" data-unit-type-picker role="radiogroup"
           aria-label="${esc(t('property.unit_form.type'))}">
        ${UNIT_TYPES.map((k) => {
          const sel = currentType === k;
          return `
            <button type="button" class="unit-type-tile${sel ? ' is-selected' : ''}"
                    role="radio" aria-checked="${sel ? 'true' : 'false'}"
                    data-unit-type-pick="${esc(k)}">
              <span class="unit-type-tile-icon">${icon(unitIcon(k))}</span>
              <span class="unit-type-tile-label">${esc(t('property.unit_form.types.' + k))}</span>
            </button>`;
        }).join('')}
      </div>
      <input type="hidden" name="unit_type" value="${esc(currentType)}" />`;
    return `
      <div class="wizard-first-unit" data-first-unit>
        ${headHtml}
        <div class="field"><label>${esc(t('property.unit_form.type'))}</label>
          ${typeTilesHtml}
        </div>
        <div class="field"><label>${esc(t('property.unit_form.identifier'))}</label>
          <input class="input" name="unit_identifier" value="${esc(u.identifier || '1')}" maxlength="64" /></div>
        ${sectionHead
          ? sectionHead(
              t('property.wizard.basics.section_address') || 'Cím',
              t('property.wizard.basics.section_address_hint') || 'Az egység postai címe',
            )
          : ''}
        ${addressPasteFieldHtml('unit_')}
        <div class="field-row three">
          <div class="field"><label>${esc(t('property.unit_form.postal_code') || 'Irányítószám')}</label>
            <input class="input" name="unit_postal_code" maxlength="32" value="${esc(u.postal_code || '')}" placeholder="1062" /></div>
          <div class="field"><label>${esc(t('property.unit_form.city') || 'Város')}</label>
            <input class="input" name="unit_city" maxlength="128" value="${esc(u.city || '')}" placeholder="Budapest" /></div>
          <div class="field"><label>${esc(t('property.unit_form.country') || 'Ország')}</label>
            ${countryFieldHtml(u.country || 'HU', { name: 'unit_country' })}</div>
        </div>
        <div class="field-row">
          <div class="field" style="flex:2 1 200px"><label>${esc(t('property.unit_form.addr_street') || 'Utca, közterület')}</label>
            <input class="input" name="unit_addr_street" required maxlength="190" value="${esc(u.addr_street || '')}" placeholder="Andrássy út" /></div>
          <div class="field" style="flex:1 1 100px"><label>${esc(t('property.unit_form.addr_house_number') || 'Házszám')}</label>
            <input class="input" name="unit_addr_house_number" maxlength="32" value="${esc(u.addr_house_number || '')}" placeholder="12" /></div>
        </div>
        <div class="field-row">
          <div class="field"><label>${esc(t('property.unit_form.addr_building') || 'Épület')}</label>
            <input class="input" name="unit_addr_building" maxlength="32" value="${esc(u.addr_building || '')}" placeholder="B" /></div>
          <div class="field"><label>${esc(t('property.unit_form.addr_staircase') || 'Lépcsőház')}</label>
            <input class="input" name="unit_addr_staircase" maxlength="32" value="${esc(u.addr_staircase || '')}" /></div>
          <div class="field"><label>${esc(t('property.unit_form.floor') || 'Emelet')}</label>
            <input class="input" name="unit_floor" maxlength="16" value="${esc(u.floor || '')}" placeholder="3" /></div>
          <div class="field"><label>${esc(t('property.unit_form.addr_door') || 'Ajtó')}</label>
            <input class="input" name="unit_addr_door" maxlength="32" value="${esc(u.addr_door || '')}" placeholder="5" /></div>
        </div>
        <div class="field"><label>${esc(t('property.unit_form.helyrajzi_szam') || 'Helyrajzi szám')} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${esc(t('common.optional'))})</span></label>
          <input class="input" name="unit_helyrajzi_szam" maxlength="64" placeholder="39221/27/A/118" value="${esc(u.helyrajzi_szam || '')}" /></div>
      </div>
    `;
  }

  // Step 3 — Bérlet: the first unit's rental terms (méret, szobaszám,
  // bér, közös költség, max. létszám). PUTs the seeded unit on advancing.
  // The address + identity were set on Alapadatok; bútorozottság moved to
  // the Felszereltség step (it sits with the building amenities now).
  _stepRentalHtml() {
    const u = this.state.unit || {};
    const saved = this.state.unitSaved;
    const cur = esc(this.state.basics.currency || 'HUF');
    const isApt = isResidentialType(u.type || saved?.type || 'apartment');
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          <p class="panel-lede">${esc(t('property.wizard.rental.help') || 'A bérelhető egység díjai és paraméterei. Mind opcionális — később is módosíthatod az ingatlan oldalán.')}</p>
          <form class="stack" data-rental-form autocomplete="off">
            <div class="field-row three" data-apartment-only ${isApt ? '' : 'hidden'}>
              <div class="field"><label>${esc(t('property.unit_form.size'))}</label>
                <input class="input" name="size_sqm" type="number" step="0.01" min="0" value="${esc(u.size_sqm || '')}" /></div>
              <div class="field"><label>${esc(t('property.unit_form.room_count') || 'Szobaszám')} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">${esc(t('property.unit_form.room_count_hint') || 'pl. 2+1')}</span></label>
                <input class="input" name="room_count" maxlength="16" value="${esc(u.room_count || '')}" placeholder="2+1" /></div>
              <div class="field"><label>${esc(t('property.unit_form.max_occupants') || 'Max. létszám')}</label>
                <input class="input" name="max_occupants" type="number" step="1" min="0" value="${esc(u.max_occupants || '')}" placeholder="—" /></div>
            </div>
            <div class="field-row">
              <div class="field"><label>${esc(t('property.unit_form.asking_rent'))} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${cur})</span></label>
                <input class="input" name="asking_rent" type="number" step="1" min="0" value="${esc(u.asking_rent || '')}" /></div>
              <div class="field"><label>${esc(t('property.unit_form.kozos_koltseg') || 'Közös költség / hó')} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${cur})</span></label>
                <input class="input" name="kozos_koltseg" type="number" step="1" min="0" value="${esc(u.kozos_koltseg || '')}" placeholder="0" /></div>
            </div>
          </form>
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.rental.note_label') || 'Bérleti díj vs. tényleges')}</span>
            ${esc(t('property.wizard.rental.note_help') || 'Az itt megadott bérleti díj a meghirdetett ár — a tényleges (esetleg alkudott) díj a bérleti szerződésen rögzül. A közös költség minden havi díjbekérőn automatikusan megjelenik.')}
          </div>
        </aside>
      </div>
    `;
  }

  // Step — Energetika: the first unit's energy certificate. Per-unit (each
  // albetét has its own E-tanúsítvány). If the unit already has a HRSZ +
  // város (typed or from the deed), we auto-look it up on OÉNY when the step
  // mounts; the HET-code field still triggers a lookup on blur.
  _stepEnergyHtml() {
    const u = this.state.unit || {};
    const hasHrsz = !!((u.helyrajzi_szam || '').trim() && (u.city || '').trim());
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          <p class="panel-lede">${esc(t('property.wizard.energy.help') || 'Az egység energetikai tanúsítványa. Ha megvan a helyrajzi szám és a város, automatikusan lekérjük az OÉNY-ből — egyébként írd be a HET-kódot és kitöltjük.')}</p>
          <form class="stack" data-energy-form autocomplete="off">
            ${energySectionHtml({
              energy_rating: u.energy_rating,
              energy_certificate_number: u.energy_certificate_number,
              energy_certificate_expires_at: u.energy_certificate_expires_at,
            }, { showDropzone: false })}
            <div data-energy-lookup-status class="faint" style="font-size:12.5px; min-height:18px;"></div>
          </form>
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.basics.note_energy_label') || 'Energetikai tanúsítvány · jogi')}</span>
            ${esc(t('property.wizard.basics.note_energy_help') || 'Minden bérleti hirdetéshez kötelező. A tanúsítvány 10 évig érvényes; a Nestly 60 nappal lejárat előtt szól.')}
          </div>
          <div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.energy.note_lookup_label') || 'Automatikus lekérés')}</span>
            ${esc(hasHrsz
              ? (t('property.wizard.energy.note_lookup_have') || 'A helyrajzi szám alapján megpróbáljuk automatikusan kitölteni az energetikai osztályt és a lejáratot.')
              : (t('property.wizard.energy.note_lookup_missing') || 'Add meg a helyrajzi számot és a várost az Alapadatoknál az automatikus lekéréshez, vagy írd be ide a HET-kódot.'))}
          </div>
        </aside>
      </div>
    `;
  }

  _stepMetersHtml() {
    const u = this.state.unitSaved;
    const propertyId = this.state.propertyId || '';
    if (!u) {
      return `<div class="wizard-cols"><div class="wizard-col-main"><div class="muted">${esc(t('common.loading') || 'Loading…')}</div></div></div>`;
    }
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          ${this._listingReadyBanner()}
          <p class="panel-lede">${esc(t('property.wizard.meters.help') || 'A mérők az egységhez tartoznak — a leolvasási előzmény túléli a bérlőváltást. Add hozzá most a jelenlegi mérőket a kezdő állásukkal, és a havi díjbekérőkön automatikusan megjelennek.')}</p>
          <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.meters.for_unit') || 'Mérők ehhez az egységhez')} · <strong>${esc(u.identifier || '—')}</strong></div>
          <div data-unit-meters-slot="${esc(u.id)}"></div>
        </div>
        <aside class="wizard-col-side">
          <div class="note accent">
            <span class="note-l">${esc(t('property.wizard.units.note_meters_label') || 'Miért itt vannak a mérők')}</span>
            ${esc(t('property.wizard.units.note_meters_help') || 'A mérők az egységhez tartoznak, nem a bérleti szerződéshez — így a leolvasási előzmény túléli a bérlőváltást. Regisztráld őket most a kezdő állással, és az első naptól készen állnak.')}
          </div>
          <div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.meters.note_skip_label') || 'Kihagyhatod')}</span>
            ${esc(t('property.wizard.meters.note_skip_help') || 'Ha most nem tudod az aktuális állásokat, ugorj a Kész lépésre — később az ingatlan oldaláról bármikor hozzáadhatod.')}
          </div>
          ${propertyId ? `<div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.meters.note_more_units_label') || 'Több egység?')}</span>
            ${esc(t('property.wizard.meters.note_more_units_help') || 'További egységeket, dokumentumokat és tulajdonosokat az ingatlan oldalán adhatsz hozzá.')}
            <div style="margin-top:8px;"><a class="btn-mono" href="#/properties/${esc(propertyId)}">${esc(t('property.wizard.meters.open_property') || 'Ingatlan megnyitása')}</a></div>
          </div>` : ''}
        </aside>
      </div>
    `;
  }


  _stepTitleDeedHtml() {
    const files = Array.isArray(this.state.titleDeedFiles) ? this.state.titleDeedFiles : [];
    const loading = this.state.titleDeedLoading;
    const err = this.state.titleDeedError;
    const fields = this.state.ocrFields;
    const scanLabel = files.length > 1
      ? t('property.wizard.title_deed.scanning_pages', { count: files.length })
      : t('property.wizard.title_deed.scanning');
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          <p class="panel-lede">${esc(t('property.wizard.title_deed.help'))}</p>
          <div class="field">
            <label>${esc(t('property.wizard.title_deed.file_label'))}</label>
            ${filePickerHtml({
              inputAttrs: 'accept="image/*,application/pdf" multiple data-role="title-deed-file"',
              files,
              disabled: loading,
              variant: 'drop-zone',
              subLabel: t('property.wizard.title_deed.file_sub'),
            })}
            <p class="drop-auto-hint mono">${esc(t('property.wizard.title_deed.auto_run_hint') || 'Reading begins the moment a file is dropped. No click needed.')}</p>
          </div>
          ${loading ? `<div class="muted"><span class="spinner"></span> ${esc(scanLabel)}</div>` : ''}
          ${err ? `<div class="error-banner">${esc(err)}</div>` : ''}
          ${fields ? this._ocrPreviewHtml(fields) : ''}
          ${(!loading && !fields) ? `
            <div class="deed-skip" data-role="deed-skip">
              <div class="deed-skip-or"><span>${esc(t('property.wizard.title_deed.or') || 'vagy')}</span></div>
              <button type="button" class="btn btn-outline deed-skip-btn" data-action="skip">
                <span>${esc(t('property.wizard.title_deed.skip_cta') || 'Nincs tulajdoni lapom — kihagyom')}</span>${icon('arrow-right', { size: 'sm' })}
              </button>
              <p class="faint deed-skip-hint">${esc(t('property.wizard.title_deed.skip_hint') || 'Az adatokat kézzel is megadhatod, a tulajdoni lapot pedig később az ingatlan oldalán is feltöltheted.')}</p>
            </div>` : ''}
        </div>
        <aside class="wizard-col-side">
          <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.title_deed.extracts_title') || 'OCR will extract')}</div>
          <div class="data-row"><span class="data-row-n">01</span><div><div class="data-row-t">${esc(t('property.wizard.title_deed.extract_address') || 'Full property address')}</div></div></div>
          <div class="data-row"><span class="data-row-n">02</span><div><div class="data-row-t">${esc(t('property.wizard.title_deed.extract_parcel') || 'Parcel / HR number')}</div></div></div>
          <div class="data-row"><span class="data-row-n">03</span><div><div class="data-row-t">${esc(t('property.wizard.title_deed.extract_area') || 'Total area (m²)')}</div></div></div>
          <div class="data-row"><span class="data-row-n">04</span><div><div class="data-row-t">${esc(t('property.wizard.title_deed.extract_owners') || 'Owner names & shares')}</div></div></div>
          <div class="note" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.title_deed.note_label') || 'Validation')}</span>
            ${esc(t('property.wizard.title_deed.multi_deed_hint'))}
          </div>
          <div class="note accent" style="margin-top: 18px;">
            <span class="note-l">${esc(t('property.wizard.title_deed.auto_label') || 'Automatic')}</span>
            ${esc(t('property.wizard.title_deed.file_help'))}
          </div>
        </aside>
      </div>
    `;
  }

  // Derive the per-unit slices that downstream code (preview + auto-create)
  // works against. When the model emitted a top-level units[] we trust
  // that; otherwise we synthesise a single slice from the flat fields so
  // legacy single-unit deeds keep working unchanged.
  _unitSlicesFromOcr() {
    const f = this.state.ocrFields || {};
    if (Array.isArray(f.units) && f.units.length > 0) {
      return f.units.filter((u) => u && typeof u === 'object');
    }
    const owners = Array.isArray(f.owners) ? f.owners.filter((o) => o && o.is_current !== false) : [];
    if (f.area_sqm || owners.length > 0 || f.helyrajzi_szam) {
      return [{
        helyrajzi_szam: f.helyrajzi_szam || f.parcel_number,
        address: f.address,
        area_sqm: f.area_sqm,
        floor: f.floor,
        door: f.door,
        owners,
      }];
    }
    return [];
  }

  // Suggest a short, human-readable identifier for an auto-created unit.
  // Prefers the within-building locator (floor/door) when present, then
  // the helyrajzi sz\u00e1m tail, then a sequential fallback. The user
  // can edit it on the Units step after creation.
  _suggestUnitIdentifier(slice, idx) {
    const d = slice && slice.door ? String(slice.door).trim() : '';
    const fl = slice && slice.floor ? String(slice.floor).trim() : '';
    if (fl && d) return `${fl}/${d}`;
    if (d) return d;
    if (fl) return fl;
    const hrsz = slice && slice.helyrajzi_szam ? String(slice.helyrajzi_szam).trim() : '';
    if (hrsz) {
      const parts = hrsz.split('/').filter(Boolean);
      if (parts.length >= 2) return parts.slice(-2).join('/');
      if (parts.length === 1) return parts[0];
    }
    return `A${idx + 1}`;
  }

  _ocrPreviewHtml(fields) {
    // When the model says the image isn't a tulajdoni lap, refuse to apply
    // and show the reason. The fields[] block is suppressed because copying
    // names off an ID card / lakc\u00edmk\u00e1rtya / contract is exactly
    // the bug we are trying to avoid.
    const docType = typeof fields.document_type === 'string' ? fields.document_type : null;
    if (docType && docType !== 'title_deed') {
      const reason = fields.document_type_reason ? String(fields.document_type_reason) : '';
      return `
        <div class="card stack" data-role="ocr-not-title-deed">
          <strong>${esc(t('property.wizard.title_deed.not_title_deed_title'))}</strong>
          <div class="muted">${esc(t('property.wizard.title_deed.not_title_deed_help'))}</div>
          ${reason ? `<div class="faint">${esc(reason)}</div>` : ''}
        </div>
      `;
    }
    const slices = this._unitSlicesFromOcr();
    const applyButton = this.state.ocrApplied
      ? `<div class="muted">${esc(t('property.wizard.title_deed.applied'))}</div>`
      : `<div><button type="button" class="btn btn-primary" data-action="apply-ocr">
          ${icon('check', { size: 'sm' })}<span>${esc(t('property.wizard.title_deed.apply'))}</span>
         </button></div>`;
    if (slices.length > 1) return this._ocrMultiUnitPreviewHtml(fields, slices, applyButton);
    return this._ocrSingleUnitPreviewHtml(fields, slices[0] || {}, applyButton);
  }

  _ocrSingleUnitPreviewHtml(fields, slice, applyButton) {
    const owners = Array.isArray(slice.owners)
      ? slice.owners.filter((o) => o && o.is_current !== false)
      : (Array.isArray(fields.owners) ? fields.owners.filter((o) => o && o.is_current !== false) : []);
    const ownersHtml = owners.length === 0
      ? `<div class="empty">${esc(t('property.wizard.title_deed.no_owners'))}</div>`
      : `<div class="list">${owners.map((o) => `
          <div class="list-item">
            ${icon('user', { class: 'list-leading icon-faint' })}
            <div class="stack-2 grow">
              <strong>${esc(o.name || '\u2014')}</strong>
              <span class="faint">${o.share ? esc(String(o.share)) : ''}</span>
            </div>
          </div>`).join('')}</div>`;
    const addr = esc(slice.address || fields.address || '\u2014');
    const hrsz = esc(slice.helyrajzi_szam || fields.helyrajzi_szam || fields.parcel_number || '\u2014');
    return `
      <div class="card stack" data-role="ocr-preview">
        <strong>${esc(t('property.wizard.title_deed.preview_title'))}</strong>
        <div><span class="faint">${esc(t('property.wizard.title_deed.address'))}:</span> ${addr}</div>
        <div><span class="faint">${esc(t('property.wizard.title_deed.parcel'))}:</span> ${hrsz}</div>
        <div class="stack-2">
          <span class="faint">${esc(t('property.wizard.title_deed.owners'))}</span>
          ${ownersHtml}
        </div>
        ${applyButton}
      </div>
    `;
  }

  // Multi-unit deed preview: one row per detected albet\u00e9t with
  // suggested identifier, area and owner count. The user can still hit
  // Apply to pre-fill the building's address into Basics; the per-unit
  // rows themselves are created automatically right after Basics is
  // submitted (see _autoCreateUnitsFromOcr below).
  _ocrMultiUnitPreviewHtml(fields, slices, applyButton) {
    const count = slices.length;
    const rows = slices.map((slice, i) => {
      const ident = esc(this._suggestUnitIdentifier(slice, i));
      const area = slice.area_sqm != null && slice.area_sqm !== ''
        ? ` \u00b7 ${esc(String(slice.area_sqm))} m\u00b2` : '';
      const hrsz = slice.helyrajzi_szam ? ` \u00b7 HRSZ ${esc(String(slice.helyrajzi_szam))}` : '';
      const owners = Array.isArray(slice.owners)
        ? slice.owners.filter((o) => o && o.is_current !== false) : [];
      const ownerCount = owners.length
        ? ` \u00b7 ${esc(t('property.wizard.title_deed.owner_count', { count: owners.length }))}` : '';
      return `
        <div class="list-item" data-role="ocr-unit">
          ${icon('home', { class: 'list-leading icon-faint' })}
          <div class="stack-2 grow">
            <strong>${ident}</strong>
            <span class="faint">${hrsz}${area}${ownerCount}</span>
          </div>
        </div>`;
    }).join('');
    const buildingAddr = esc(fields.address || '\u2014');
    return `
      <div class="card stack" data-role="ocr-preview-multi">
        <strong>${esc(t('property.wizard.title_deed.multi_preview_title', { count }))}</strong>
        <div><span class="faint">${esc(t('property.wizard.title_deed.building_address'))}:</span> ${buildingAddr}</div>
        <div class="list">${rows}</div>
        <div class="muted">${esc(t('property.wizard.title_deed.multi_auto_create_hint'))}</div>
        ${applyButton}
      </div>
    `;
  }

  _stepDoneHtml() {
    const p = this.state.property || {};
    const unitCount = this.state.unitSaved ? 1 : 0;
    const deedCount = (this.state.titleDeedFiles || []).length;
    const propertyId = this.state.propertyId || p.id || '';
    // Show a recap of sections the chosen mode skipped so users who picked
    // Quick / Full know where to find them later. 'done' isn't a real section
    // and 'basics' is always visited, so they don't show up here.
    const mode = MODES.includes(this.state.mode) ? this.state.mode : DEFAULT_MODE;
    const visited = new Set(MODE_FLOWS[mode]);
    const skippable = ['title_deed', 'rental', 'energy', 'details', 'owners', 'meters'];
    const skipped = skippable.filter((k) => !visited.has(k));
    const skippedHtml = (skipped.length > 0 && propertyId) ? `
      <div class="note" style="margin-top: 22px;">
        <span class="note-l">${esc(t('property.wizard.done.skipped_title') || 'Mit hagyhatsz hozzá később')}</span>
        <p style="margin: 4px 0 10px;">${esc(t('property.wizard.done.skipped_intro') || 'Az ingatlan oldalán bármikor pótolhatod ezeket:')}</p>
        <ul class="list" style="margin: 0; padding-left: 18px;">
          ${skipped.map((k) => `<li>${esc(t('property.wizard.done.skipped_' + k) || k)}</li>`).join('')}
        </ul>
      </div>` : '';
    return `
      <div class="wizard-cols">
        <div class="wizard-col-main">
          <span class="badge success" style="margin-bottom: 14px;"><span class="dot-anim"></span>${esc(t('property.wizard.done.recorded') || 'Recorded')}</span>
          <h3 class="done-headline">
            <span class="panel-title-acc">${esc(p.name || p.address_line1 || '—')}</span>${esc(t('property.wizard.done.filed') || ', filed.')}
          </h3>
          <p class="panel-lede">${esc(unitCount)} ${esc(t('property.wizard.done.units_label') || 'unit(s)')} · ${esc(deedCount)} ${esc(t('property.wizard.done.deeds_label') || 'title deed(s) attached')}.</p>
          <div class="row" style="gap:var(--space-3); margin-top: 18px;">
            <a class="btn btn-primary" href="#/properties/${esc(propertyId)}">
              <span>${esc(t('property.wizard.done.open'))}</span>${icon('arrow-right', { size: 'sm' })}
            </a>
            <a class="btn btn-ghost" href="#/properties">${esc(t('property.wizard.done.back_to_list'))}</a>
          </div>
          ${skippedHtml}
        </div>
        <aside class="wizard-col-side">
          <pre class="code"><span class="code-d">// ${esc(t('property.wizard.done.code_comment') || 'committed to record')}</span>
POST /properties              <span class="code-ok">→ 201</span>
${unitCount > 0 ? `POST /properties/{id}/units   <span class="code-ok">→ 201</span> × ${unitCount}\n` : ''}${deedCount > 0 ? `POST /units/{id}/documents    <span class="code-ok">→ 201</span> × ${deedCount}` : ''}</pre>
          <div class="note" style="margin-top: 20px;">
            <span class="note-l">${esc(t('property.wizard.done.next_steps_title'))}</span>
            ${esc(t('property.wizard.done.next_steps_help'))}
          </div>
        </aside>
      </div>
    `;
  }

  mount() {
    const on = (sel, ev, fn) => this.on(sel, ev, fn);

    // Mode picker — three big cards on the very first /properties/new
    // entry. Clicking one sets the mode and re-renders into the wizard.
    on('[data-mode-pick]', 'click', (e) => {
      const pick = e.currentTarget.getAttribute('data-mode-pick');
      if (!MODES.includes(pick)) return;
      // Explicit picker click means "start this mode from step 1". A saved
      // draft (mid-flow refresh recovery) may still carry a later step key —
      // suppress that restore so the user always lands on step 1 of the
      // chosen mode. Form data (basics, OCR fields, staged files) still
      // hydrates normally; only the step number is forced.
      this._suppressDraftStepRestore = true;
      this.setState({ mode: pick, step: 1 });
      this._syncUrl(1);
    });
    // When the picker is up, nothing else on this page is mounted yet —
    // skip the rest of the wiring (the form/slot selectors below would
    // all miss anyway since the panel-body holds picker cards instead).
    if (this.state.mode === null) return;

    // One-shot hydrate: pull the saved draft + staged file rows the very
    // first time this instance lands in the DOM. Subsequent re-renders
    // (setState) skip this branch.
    if (!this._hydratedOnce) {
      this._hydratedOnce = true;
      // Fire-and-forget; the hydrate path calls setState when data arrives,
      // which re-runs mount() — guarded by _hydratedOnce above.
      this._hydrate();
    }

    // Wizard navigation — back/next/skip + clickable past steps in the stepper.
    on('[data-action="back"]', 'click', () => this._goToStep(this.state.step - 1));
    on('[data-action="next"]', 'click', () => this._tryAdvance());
    on('[data-action="skip"]', 'click', () => this._goToStep(this.state.step + 1));
    on('[data-step-jump]', 'click', (e) => {
      const target = Number(e.currentTarget.getAttribute('data-step-jump')) || 1;
      if (target < this.state.step) this._goToStep(target);
    });

    // Step 1 — basics: capture input values into instance state so the
    // submit handler can read them without rebuilding the form data.
    // Energy rating + amenity wiring lives in PropertyBasicsFields so the
    // edit form (PropertyView) and the wizard stay in sync.
    const basicsForm = this.el.querySelector('[data-basics-form]');
    if (basicsForm) {
      const routeInput = (name, value) => {
        if (name === 'energy_rating' || name === 'energy_rating_custom') return;
        if (name === 'country_label') return;
        if (name.endsWith('address_paste')) return;
        if (name.startsWith('unit_')) {
          this.state.unit[name.slice(5)] = value;
        } else {
          this.state.basics[name] = value;
        }
      };
      basicsForm.querySelectorAll('input').forEach((inp) => {
        inp.addEventListener('input', (e) => routeInput(e.target.name, e.target.value));
      });
      basicsForm.querySelectorAll('select').forEach((sel) => {
        sel.addEventListener('change', (e) => {
          const name = e.target.name;
          if (name === 'energy_rating_preset') return;
          routeInput(name, e.target.value);
        });
      });
      // Unit-type tile picker: click a tile to update state.unit.type,
      // toggle the visual selection across siblings, mirror the value
      // into the hidden form input, and refresh apartment-only field
      // visibility — the same behaviours the old <select> change handler
      // covered before the tiles replaced it.
      const typePicker = basicsForm.querySelector('[data-unit-type-picker]');
      if (typePicker) {
        const hidden = basicsForm.querySelector('input[type="hidden"][name="unit_type"]');
        typePicker.addEventListener('click', (e) => {
          const tile = e.target.closest('[data-unit-type-pick]');
          if (!tile) return;
          const pick = tile.getAttribute('data-unit-type-pick');
          if (!pick) return;
          routeInput('unit_type', pick);
          if (hidden) hidden.value = pick;
          typePicker.querySelectorAll('[data-unit-type-pick]').forEach((el) => {
            const isSel = el === tile;
            el.classList.toggle('is-selected', isSel);
            el.setAttribute('aria-checked', isSel ? 'true' : 'false');
          });
          const isApt = isResidentialType(pick);
          this.el.querySelectorAll('[data-first-unit] [data-apartment-only]').forEach((el) => { el.hidden = !isApt; });
        });
      }
      // The canonical address now lives on the first unit (unit_-prefixed
      // inputs), so the postal→city autofill drives those and mirrors into
      // state.unit rather than state.basics.
      wireAddressFields(basicsForm, {
        prefix: 'unit_',
        onChange: ({ postal_code, city, country }) => {
          this.state.unit.postal_code = postal_code;
          this.state.unit.city = city;
          this.state.unit.country = country;
        },
      });
    }

    // Step "Bérlet": rental terms for the first unit. Inputs route into
    // state.unit (un-prefixed names here, unlike the basics step).
    const rentalForm = this.el.querySelector('[data-rental-form]');
    if (rentalForm) {
      rentalForm.querySelectorAll('input,select').forEach((inp) => {
        const evt = inp.tagName === 'SELECT' ? 'change' : 'input';
        inp.addEventListener(evt, (e) => {
          if (e.target.name) this.state.unit[e.target.name] = e.target.value;
        });
      });
    }

    // Energetika step — the first unit's energy certificate. Inputs route
    // into state.unit; HET-code blur triggers an OÉNY lookup, and when the
    // unit already has a HRSZ + város we kick off a topographical lookup as
    // the step mounts.
    const energyForm = this.el.querySelector('[data-energy-form]');
    if (energyForm) {
      energyForm.querySelectorAll('input').forEach((inp) => {
        inp.addEventListener('input', (e) => {
          const name = e.target.name;
          if (!name || name === 'energy_rating' || name === 'energy_rating_custom') return;
          this.state.unit[name] = e.target.value;
        });
      });
      wireEnergyRatingInputs(energyForm, {
        onChange: (v) => { this.state.unit.energy_rating = v; },
      });
      wireEnergyCertVerify(energyForm, {
        onMatch: (row) => this._applyEnergyLookupRow(energyForm, row),
      });
      // Auto-lookup from HRSZ + város on mount (only when energy is still empty).
      this._lookupEnergyByHrsz(energyForm).catch(() => {});
    }

    // Felszereltség step — bútorozottság + amenities both belong to the unit
    // now (the property's amenities column is a cache refreshed from the
    // primary unit by syncAddressFromMainUnit).
    const detailsForm = this.el.querySelector('[data-details-form]');
    if (detailsForm) {
      wireFurnishedRadio(detailsForm, {
        onChange: (v) => { this.state.unit.furnished = v; },
      });
      wireAmenitiesChips(detailsForm, {
        initial: Array.isArray(this.state.unit.amenities) ? this.state.unit.amenities : [],
        onChange: (list) => { this.state.unit.amenities = list; },
      });
    }

    // Step 3 — details also hosts the property photos / documents panel
    // (PHOTO + OTHER + AUTHORIZATION). Mounted against the already-created
    // property; uploads happen immediately through the panel's own API.
    if (this._docsPanel) { try { this._docsPanel.destroy?.(); } catch {} this._docsPanel = null; }
    if (this.state.propertyId && this.el.querySelector('[data-documents-slot]')) {
      this._docsPanel = attachDocumentsPanel(this.el, { propertyId: this.state.propertyId, canManage: true });
    }

    // Owners + Meters steps both render slot divs against the seeded
    // unit; mount the corresponding panels here. Tears down any previous
    // instances first so their poll timers are released before fresh
    // panels attach. The shared UnitExternalOwnersPanel + UnitMetersPanel
    // are the same components used on the property-detail page.
    this._destroyUnitPanels();
    if (this.state.unitSaved) {
      const uid = this.state.unitSaved.id;
      const owners = attachUnitExternalOwnersPanel(this.el, { unitId: uid, canManage: true });
      const meters = attachUnitMetersPanel(this.el, { unitId: uid, canManage: true });
      if (owners || meters) this._unitPanels[uid] = { owners, meters };
    }

    // Issuer step — kick off the load once the step is on screen, then
    // wire the pick radios and the invite form. Selection persists
    // immediately (PUT /properties/{id}/landlord), so "next" is free.
    if (this.el.querySelector('[data-step-key="issuer"]')) {
      if (this.state.issuer === null && this.state.propertyId) this._loadIssuer();
      on('[data-issuer-user]', 'change', (e) => this._pickIssuer({ userId: e.currentTarget.getAttribute('data-issuer-user') }));
      on('[data-issuer-ext]', 'change', (e) => this._pickIssuer({ extId: e.currentTarget.getAttribute('data-issuer-ext') }));
      const inviteInput = this.el.querySelector('[data-issuer-invite-email]');
      if (inviteInput) {
        inviteInput.addEventListener('input', (e) => {
          if (this.state.issuer) this.state.issuer.inviteEmail = e.target.value;
        });
      }
      on('[data-action="issuer-invite"]', 'click', () => this._inviteIssuer());
    }
    // Step 1 — title deed: file picker, on-demand OCR, apply-extracted button.
    // The label-wrapped hidden input handles its own click semantics; we only
    // hook the change event and the inline "clear" button.
    const fileInput = this.el.querySelector('[data-role="title-deed-file"]');
    if (fileInput) {
      fileInput.addEventListener('change', (e) => this._onTitleDeedPicked(e));
    }
    wireDropZones(this.el);
    on('[data-action="clear-file"]', 'click', (e) => {
      e.preventDefault();
      this._clearTitleDeedFile();
    });
    on('[data-action="apply-ocr"]', 'click', () => this._applyOcrFields());

  }

  // ---- Step transitions ----

  _goToStep(step) {
    const flow = this._stepFlow();
    const next = Math.min(Math.max(1, Number(step) || 1), flow.length);
    if (next === this.state.step) return;
    const previous = this.state.step;
    clearFormError(this.el);
    // Persist before transitioning so a refresh on the next step still sees
    // the latest payload. Best-effort: a failed save (offline, 5xx) shouldn't
    // block the user from advancing through the wizard.
    this._persistDraft(next).catch(() => {});
    // Stage any freshly-picked title-deed bytes when leaving step 1 so they
    // survive a later page refresh. Background, fire-and-forget.
    if (previous === 1 && next !== 1) {
      this._stageTitleDeedFiles().catch(() => {});
    }
    if (next === flow.length) {
      // Reached the Done step — archive the draft.
      completeDraft(KIND, this.state.propertyId || null).catch(() => {});
    }
    this.setState({ step: next });
    this._syncUrl(next);
  }

  async _tryAdvance() {
    const flow = this._stepFlow();
    const key = flow[this.state.step - 1].key;
    if (key === 'basics') return this._submitBasics();
    if (key === 'rental') return this._submitRental();
    if (key === 'energy') return this._submitEnergy();
    if (key === 'details') return this._submitDetails();
    return this._goToStep(this.state.step + 1);
  }

  // ---- Issuer step (agency staff) ----

  // Parallel fetch of the property row (current landlord_* designation),
  // its memberships (eligible OWNER/LANDLORD users + pending invitations)
  // and every unit's external deed owners.
  async _loadIssuer() {
    const id = this.state.propertyId;
    if (!id) return;
    this.state.issuer = { loading: true };
    this.setState({});
    try {
      const [property, memberships, owners] = await Promise.all([
        api.get(`/properties/${encodeURIComponent(id)}`),
        api.get(`/properties/${encodeURIComponent(id)}/memberships`),
        api.get(`/properties/${encodeURIComponent(id)}/unit-external-owners`),
      ]);
      this.setState({ issuer: {
        loading: false,
        error: null,
        landlordUserId: property.landlord_user_id || null,
        landlordExternalOwnerId: property.landlord_external_owner_id || null,
        members: (memberships && memberships.data) || [],
        pending: (memberships && memberships.pending) || [],
        owners: (owners && owners.data) || [],
        inviteEmail: '',
        inviteBusy: false,
        inviteError: null,
        inviteNotice: null,
      } });
    } catch (err) {
      this.setState({ issuer: {
        loading: false, members: [], pending: [], owners: [],
        error: err instanceof ApiError ? err.message : t('property.wizard.issuer.errors.load'),
      } });
    }
  }

  // Persist the designation immediately — member user and external owner
  // are mutually exclusive (the server clears the other side).
  async _pickIssuer({ userId = null, extId = null }) {
    const id = this.state.propertyId;
    const iss = this.state.issuer;
    if (!id || !iss || iss.loading) return;
    const prevUser = iss.landlordUserId;
    const prevExt = iss.landlordExternalOwnerId;
    iss.landlordUserId = userId || null;
    iss.landlordExternalOwnerId = extId || null;
    iss.error = null;
    this.setState({});
    try {
      await api.put(`/properties/${encodeURIComponent(id)}/landlord`, userId
        ? { landlord_user_id: userId }
        : { landlord_external_owner_id: extId });
    } catch (err) {
      iss.landlordUserId = prevUser;
      iss.landlordExternalOwnerId = prevExt;
      iss.error = err instanceof ApiError ? err.message : t('property.wizard.issuer.errors.set');
      this.setState({});
    }
  }

  // Invite the (not-yet-registered) owner by email as an OWNER member.
  // Existing accounts get the membership immediately and appear in the
  // eligible list; unknown emails land as a pending invitation.
  async _inviteIssuer() {
    const id = this.state.propertyId;
    const iss = this.state.issuer;
    if (!id || !iss || iss.inviteBusy) return;
    const email = (iss.inviteEmail || '').trim();
    if (!email) return;
    iss.inviteBusy = true;
    iss.inviteError = null;
    iss.inviteNotice = null;
    this.setState({});
    try {
      const res = await api.post(`/properties/${encodeURIComponent(id)}/memberships`, { email, role: 'OWNER' });
      if (res && res.pending) {
        iss.pending = [...(iss.pending || []), { email }];
        iss.inviteNotice = t('property.wizard.issuer.invite_sent', { email });
      } else if (res) {
        iss.members = [...(iss.members || []), res];
        iss.inviteNotice = t('property.wizard.issuer.invite_added', { email });
      }
      iss.inviteEmail = '';
      iss.inviteBusy = false;
      this.setState({});
    } catch (err) {
      iss.inviteBusy = false;
      iss.inviteError = err instanceof ApiError ? err.message : t('property.wizard.issuer.errors.invite');
      this.setState({});
    }
  }


  // Persist the first unit's rental terms (méret, szobaszám, bér, közös,
  // bútorozottság, max) via PUT, then advance. Best-effort: empty fields
  // just move on — all optional.
  async _submitRental() {
    if (this._busy) return;
    const saved = this.state.unitSaved;
    if (!saved) return this._goToStep(this.state.step + 1);
    const u = this.state.unit || {};
    const rentMajor = parseFloat(String(u.asking_rent || ''));
    const kozosMajor = parseFloat(String(u.kozos_koltseg || ''));
    const maxOcc = parseInt(String(u.max_occupants || ''), 10);
    // Merge onto the saved row so the address/energy/furnished set on other
    // steps survive this full-replace PUT.
    const body = this._unitBodyFromRow(saved, {
      size_sqm: (u.size_sqm || '').toString().trim() || null,
      room_count: (u.room_count || '').toString().trim() || null,
      asking_rent: Number.isFinite(rentMajor) ? Math.round(rentMajor * 100) : null,
      kozos_koltseg: Number.isFinite(kozosMajor) ? Math.round(kozosMajor * 100) : null,
      max_occupants: Number.isFinite(maxOcc) && maxOcc >= 0 ? maxOcc : null,
    });
    this._busy = true;
    clearFormError(this.el);
    try {
      const updated = await api.put(`/units/${encodeURIComponent(saved.id)}`, body);
      this._busy = false;
      this.setState({ unitSaved: { ...saved, ...updated } });
      this._goToStep(this.state.step + 1);
    } catch (err) {
      this._busy = false;
      showFormError(this.el, err instanceof ApiError ? err.message : t('property.wizard.errors.unit_save'));
    }
  }

  // Mirror an OÉNY result row into state.unit (used by the HET-code blur
  // path, where wireEnergyCertVerify has already filled the DOM). Only fills
  // empties so it never clobbers a manual entry.
  _applyEnergyLookupRow(formEl, row) {
    if (!row) return;
    const u = this.state.unit;
    if (row.expirationDate && !(u.energy_certificate_expires_at || '').trim()) {
      u.energy_certificate_expires_at = String(row.expirationDate).slice(0, 10);
    }
    if (row.energyClassification && !(u.energy_rating || '').trim()) {
      u.energy_rating = String(row.energyClassification).toUpperCase();
    }
    if (row.hetString && !(u.energy_certificate_number || '').trim()) {
      u.energy_certificate_number = String(row.hetString);
    }
  }

  // Write an OÉNY row into both the energy form DOM and state.unit. Used by
  // the HRSZ auto-lookup, which fills from scratch (no prior DOM values).
  _writeEnergyToForm(formEl, row) {
    if (!formEl || !row) return;
    const setVal = (sel, val) => {
      const el = formEl.querySelector(sel);
      if (el && val != null && String(val) !== '') {
        el.value = val;
        el.dispatchEvent(new Event('input', { bubbles: true })); // routes into state.unit
      }
    };
    if (row.hetString) setVal('[name="energy_certificate_number"]', String(row.hetString));
    if (row.expirationDate) setVal('[name="energy_certificate_expires_at"]', String(row.expirationDate).slice(0, 10));
    if (row.energyClassification) {
      const v = String(row.energyClassification).toUpperCase();
      const hidden = formEl.querySelector('[name="energy_rating"]');
      if (hidden) hidden.value = v;
      const preset = formEl.querySelector('[name="energy_rating_preset"]');
      if (preset) {
        const opt = Array.from(preset.options).find((o) => o.value === v);
        if (opt) {
          preset.value = v;
        } else {
          preset.value = '__other__';
          const wrap = formEl.querySelector('[data-energy-other]');
          const cust = formEl.querySelector('[name="energy_rating_custom"]');
          if (wrap) wrap.hidden = false;
          if (cust) cust.value = v;
        }
      }
      this.state.unit.energy_rating = v;
    }
  }

  // Auto-look-up the energy certificate from the unit's HRSZ + város via the
  // OÉNY topographical endpoint. No-op when we lack HRSZ/város or the unit
  // already has energy data (so it never clobbers manual / prior values).
  async _lookupEnergyByHrsz(formEl) {
    const u = this.state.unit || {};
    const hrsz = (u.helyrajzi_szam || '').trim();
    const city = (u.city || '').trim();
    if (!hrsz || !city) return;
    if ((u.energy_rating || '').trim() || (u.energy_certificate_number || '').trim()) return;
    const statusEl = formEl.querySelector('[data-energy-lookup-status]');
    const setStatus = (msg) => { if (statusEl) statusEl.textContent = msg || ''; };
    setStatus(t('property.wizard.energy.looking_up') || 'Energetikai tanúsítvány keresése a helyrajzi szám alapján…');
    try {
      const res = await api.post('/energy-certificates/lookup', {
        topographical_number: hrsz,
        settlement: city,
        street_name: (u.addr_street || '').trim() || undefined,
        house_number: (u.addr_house_number || '').trim() || undefined,
      });
      const matches = Array.isArray(res?.matches) ? res.matches : [];
      if (!matches.length) {
        setStatus(t('property.wizard.energy.no_match') || 'Nem találtunk tanúsítványt a helyrajzi szám alapján — add meg kézzel a HET-kódot.');
        return;
      }
      const sorted = [...matches].sort((a, b) =>
        String(b.creationDate || b.expirationDate || '').localeCompare(String(a.creationDate || a.expirationDate || '')));
      const row = sorted.find((m) => m.is_valid) || sorted[0];
      this._writeEnergyToForm(formEl, row);
      setStatus(t('property.wizard.energy.matched') || 'Energetikai tanúsítvány megtalálva és kitöltve.');
    } catch (_) {
      setStatus('');
    }
  }

  // Energetika step: persist the first unit's energy certificate (PUT,
  // merged onto the saved row). Best-effort — all fields optional.
  async _submitEnergy() {
    if (this._busy) return;
    const saved = this.state.unitSaved;
    if (!saved) return this._goToStep(this.state.step + 1);
    const u = this.state.unit || {};
    const body = this._unitBodyFromRow(saved, {
      energy_rating: (u.energy_rating || '').toString().trim() || null,
      energy_certificate_number: (u.energy_certificate_number || '').toString().trim() || null,
      energy_certificate_expires_at: (u.energy_certificate_expires_at || '').toString().trim() || null,
    });
    this._busy = true;
    clearFormError(this.el);
    try {
      const updated = await api.put(`/units/${encodeURIComponent(saved.id)}`, body);
      this._busy = false;
      this.setState({ unitSaved: { ...saved, ...updated } });
      this._goToStep(this.state.step + 1);
    } catch (err) {
      this._busy = false;
      showFormError(this.el, err instanceof ApiError ? err.message : t('property.wizard.errors.unit_save'));
    }
  }

  // Felszereltség step: bútorozottság + amenities both PUT the first unit
  // (migration 0084 moved amenities off the property; the property column
  // is just a cache that syncAddressFromMainUnit refreshes from the
  // primary unit). Best-effort — a failed save surfaces an inline error
  // but a user who left everything blank just moves on.
  async _submitDetails() {
    if (this._busy) return;
    const id = this.state.propertyId;
    if (!id) return this._goToStep(this.state.step + 1);
    const u = this.state.unit || {};
    const saved = this.state.unitSaved;
    if (!saved) return this._goToStep(this.state.step + 1);
    this._busy = true;
    clearFormError(this.el);
    try {
      const patch = {
        amenities: Array.isArray(u.amenities) ? u.amenities : [],
      };
      if (u.furnished) patch.furnished = u.furnished;
      const body = this._unitBodyFromRow(saved, patch);
      const updated = await api.put(`/units/${encodeURIComponent(saved.id)}`, body);
      this._busy = false;
      this.setState({ unitSaved: { ...saved, ...updated } });
      this._goToStep(this.state.step + 1);
    } catch (err) {
      this._busy = false;
      const msg = err instanceof ApiError ? err.message : t('property.wizard.errors.unit_save');
      showFormError(this.el, msg);
    }
  }

  // ---- Draft persistence ----

  // Restore prior wizard state from the backend. Two sources:
  //   1. /wizard-drafts/property_create — text payload (basics, OCR fields)
  //   2. /wizard-files/property_create  — title-deed files already staged
  // Synthesises File-like entries so the file-picker shows what was there
  // before the refresh, and routes the eventual unit upload through the
  // /from-wizard-file endpoint instead of re-uploading bytes.
  async _hydrate() {
    if (this.state._hydrated || this.state._hydrating) return;
    this.state._hydrating = true;
    try {
      const [draft, files, unitsResp] = await Promise.all([
        loadDraft(KIND, this.state.propertyId || null).catch(() => null),
        listWizardFiles(KIND, 'title_deed').catch(() => []),
        this.state.propertyId
          ? api.get(`/properties/${this.state.propertyId}/units`).catch(() => null)
          : Promise.resolve(null),
      ]);
      // Accumulate the patch and only fire setState if there's actually
      // something to restore — an empty hydrate cycle would otherwise wipe
      // any error banner / inline state the user is currently looking at.
      const patch = {};
      if (draft && draft.payload && typeof draft.payload === 'object') {
        const p = draft.payload;
        // Restore the chosen entry mode. Pre-picker drafts (or drafts saved
        // before this field existed) fall back to the legacy default so the
        // wizard never lands in the picker mid-flow.
        if (MODES.includes(p.mode)) {
          patch.mode = p.mode;
        } else if (this.state.mode === null) {
          patch.mode = DEFAULT_MODE;
        }
        if (p.basics && typeof p.basics === 'object') {
          patch.basics = { ...this.state.basics, ...p.basics };
        }
        if (p.unit && typeof p.unit === 'object') {
          // Restore the first unit's basics (incl. OCR-applied address /
          // floor / door / type) so a refresh mid-wizard keeps them.
          patch.unit = { ...this.state.unit, ...p.unit };
        }
        if (p.ocrFields && typeof p.ocrFields === 'object') {
          patch.ocrFields = p.ocrFields;
        }
        if (typeof p.ocrApplied === 'boolean') patch.ocrApplied = p.ocrApplied;
        // The saved step key needs re-resolving against the (possibly newly
        // hydrated) mode's flow — different modes have different flow
        // lengths, so the constructor's step number (computed against the
        // legacy default) may not match. Prefer the URL's step key (already
        // stashed as _initialStepKey) over the draft's step, since deep
        // links are the most explicit user intent.
        const flowMode = patch.mode || this.state.mode || DEFAULT_MODE;
        // Only fall back to the draft's step when the user didn't just pick
        // a mode from the entry picker — that picker click is an explicit
        // "start at step 1" intent and must not be overridden by a stale
        // saved step. URL-based step keys (deep links / mid-wizard refresh
        // on /properties/:id/wizard/:step) still win.
        const desiredKey = this.state._initialStepKey
          || (this._suppressDraftStepRestore ? null : (typeof draft.step === 'string' ? draft.step : null));
        if (desiredKey && this.state.step === 1) {
          const idx = MODE_FLOWS[flowMode].indexOf(desiredKey);
          if (idx > 0) patch.step = idx + 1;
        }
      }
      // Restore the seeded unit on refresh so the Owners / Meters steps
      // mount their panels against the real unit instead of a loading
      // placeholder.
      if (unitsResp) {
        const list = Array.isArray(unitsResp?.data) ? unitsResp.data : (Array.isArray(unitsResp) ? unitsResp : []);
        if (list.length > 0) patch.unitSaved = list[0];
      }
      if (Array.isArray(files) && files.length > 0) {
        // Synthetic File-like rows: filePickerHtml + downstream code only
        // touches .name on each entry; we attach the staged id parallel-
        // indexed in _titleDeedStagedIds for the unit-save path.
        patch.titleDeedFiles = files.map((r) => ({
          name: r.original_name || 'document',
          size: r.size_bytes || 0,
          type: r.mime || '',
          _stagedId: r.id,
        }));
        this._titleDeedStagedIds = files.map((r) => r.id);
        // OCR fields stored on the staged row trump anything in the draft —
        // they correspond to the actual bytes the user uploaded.
        const stagedFields = files
          .map((r) => r.extracted_fields)
          .find((x) => x && typeof x === 'object');
        if (stagedFields && !patch.ocrFields) patch.ocrFields = stagedFields;
      }
      this.state._hydrating = false;
      this.state._hydrated = true;
      if (Object.keys(patch).length > 0) {
        this.setState(patch);
      }
    } catch {
      this.state._hydrating = false;
      this.state._hydrated = true;
    }
  }

  // Save the wizard's text state. Title-deed files are persisted via the
  // staging table separately, so payload only carries primitive values.
  async _persistDraft(nextStep = null) {
    // Mode picker hasn't been resolved yet — nothing meaningful to save.
    if (this.state.mode === null) return;
    const flow = this._stepFlow();
    const stepNumber = nextStep ?? this.state.step;
    const idx = Math.min(Math.max(1, stepNumber), flow.length) - 1;
    const stepKey = flow[idx] ? flow[idx].key : flow[0].key;
    const payload = {
      mode: this.state.mode,
      basics: this.state.basics,
      // Persist the first unit too — it now holds the canonical address
      // (and the OCR-applied floor/door/identifier/type). Without this the
      // unit prefill was silently lost on a pre-create draft reload, since
      // the seeded unit row doesn't exist yet to hydrate from.
      unit: this.state.unit || null,
      ocrFields: this.state.ocrFields || null,
      ocrApplied: !!this.state.ocrApplied,
    };
    try {
      await saveDraftNow(KIND, {
        entityId: this.state.propertyId || null,
        step: stepKey,
        payload,
      });
    } catch { /* offline / 5xx — wizard keeps working from in-memory state */ }
  }

  // Update the address bar without re-running the router. We use raw
  // replaceState so router._resolve() doesn't fire and tear down the
  // component we just transitioned.
  _syncUrl(stepNumber) {
    if (typeof window === 'undefined' || !window.history) return;
    const flow = this._stepFlow();
    const idx = Math.min(Math.max(1, stepNumber), flow.length) - 1;
    const stepKey = flow[idx] ? flow[idx].key : null;
    if (!stepKey) return;
    const pid = this.state.propertyId;
    // Pre-create URLs carry ?mode= so a refresh on the mode-picker-less
    // half of the wizard (after the user picked a mode but before basics
    // submit) restores the same flow instead of bouncing back to picker.
    const modeQuery = (!pid && this.state.mode) ? `?mode=${encodeURIComponent(this.state.mode)}` : '';
    const target = pid
      ? `#/properties/${encodeURIComponent(pid)}/wizard/${encodeURIComponent(stepKey)}`
      : `#/properties/new${modeQuery}`;
    if (window.location.hash === target) return;
    try {
      const url = new URL(window.location.href);
      url.hash = target;
      window.history.replaceState(null, '', url.toString());
    } catch { /* jsdom in some configs throws on replaceState */ }
  }

  // ---- Step 1: Title deed (OCR-prefill) ----

  // File picker change handler. Accepts one or many files (a tulajdoni lap
  // photographed across several phone snapshots is normal). Always discards
  // any prior OCR result and auto-triggers a fresh scan so the user never
  // has to click a second button — picking the files IS the OCR trigger.
  async _onTitleDeedPicked(e) {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    if (picked.length === 0) return;
    this._titleDeedUploaded = false;
    // Drop any prior staged ids — fresh selection invalidates the previous
    // staging cycle. The bytes themselves are cleaned up best-effort below.
    const prevStaged = this._titleDeedStagedIds.slice();
    this._titleDeedStagedIds = [];
    this.setState({
      titleDeedFiles: picked,
      titleDeedError: null,
      ocrFields: null,
      ocrApplied: false,
    });
    prevStaged.forEach((id) => { deleteWizardFile(id).catch(() => {}); });
    // OCR runs in-line; staging is deferred to step-1 exit so the file
    // pick event itself doesn't fire extra background POSTs that would
    // surprise tests / chatty logs in the common "user picks then continues
    // immediately" path.
    await this._runTitleDeedOcr();
  }

  // Background upload of every File in titleDeedFiles to the wizard-files
  // staging endpoint, so the selection survives a refresh. The id of each
  // staged row is recorded at the same index as state.titleDeedFiles; the
  // unit-save path will then prefer the /from-wizard-file endpoint over a
  // second multipart upload. No-ops if there are no files or every entry
  // is already staged (re-entry on Back/Next).
  async _stageTitleDeedFiles() {
    const files = Array.isArray(this.state.titleDeedFiles) ? this.state.titleDeedFiles : [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f || this._titleDeedStagedIds[i]) continue;
      // Synthetic file-like rows produced by hydrate already carry _stagedId.
      if (f._stagedId) { this._titleDeedStagedIds[i] = f._stagedId; continue; }
      if (typeof File !== 'undefined' && !(f instanceof File) && !(f instanceof Blob)) continue;
      try {
        const row = await stageWizardFile(KIND, 'title_deed', f);
        if (!row || !row.id) continue;
        if (this.state.titleDeedFiles[i] !== f) {
          deleteWizardFile(row.id).catch(() => {});
          continue;
        }
        this._titleDeedStagedIds[i] = row.id;
        const ocr = this.state.ocrFields;
        if (ocr && typeof ocr === 'object') {
          setWizardFileExtractedFields(row.id, ocr).catch(() => {});
        }
      } catch { /* network error → legacy multipart will still work on unit-save */ }
    }
  }

  _clearTitleDeedFile() {
    if (this.state.titleDeedLoading) return;
    this._titleDeedUploaded = false;
    const staged = this._titleDeedStagedIds.slice();
    this._titleDeedStagedIds = [];
    this.setState({
      titleDeedFiles: [],
      titleDeedError: null,
      ocrFields: null,
      ocrApplied: false,
    });
    staged.forEach((id) => { deleteWizardFile(id).catch(() => {}); });
  }

  async _runTitleDeedOcr() {
    const files = Array.isArray(this.state.titleDeedFiles) ? this.state.titleDeedFiles : [];
    if (this._busy || files.length === 0) return;
    clearFormError(this.el);
    this._busy = true;
    this.setState({ titleDeedLoading: true, titleDeedError: null });
    try {
      const fd = new FormData();
      fd.append('kind', 'TITLE_DEED');
      // files[] semantics so the controller normalises into a list and the
      // service runs a single multi-page OCR call across every page.
      for (const f of files) {
        fd.append('files[]', f, f.name || 'document');
      }
      const res = await api.post('/properties/ocr-preview', fd);
      const fields = (res && res.extracted_fields) || null;
      // Hard-error path: the model decided this isn't a tulajdoni lap
      // (could be an ID card, a contract, a screenshot of something
      // else). Clear ocrFields so the preview block doesn't render
      // with potentially-misleading partial data — the user must pick
      // a real deed before the wizard will pre-fill anything.
      const docType = fields && typeof fields.document_type === 'string' ? fields.document_type : null;
      if (fields && docType && docType !== 'title_deed') {
        const reason = fields.document_type_reason ? ` — ${String(fields.document_type_reason)}` : '';
        this.setState({
          titleDeedLoading: false,
          ocrFields: null,
          titleDeedError: (t('property.wizard.title_deed.not_title_deed_error') || 'Ez nem egy tulajdoni lap. Tölts fel egy tulajdoni lap PDF-et vagy fotót.') + reason,
        });
        return;
      }
      this.setState({
        titleDeedLoading: false,
        ocrFields: fields,
        titleDeedError: fields ? null : t('property.wizard.title_deed.no_data'),
      });
      // Auto-apply on success — no explicit button click required — then
      // jump straight to the Basics step with the form pre-filled. The
      // user reviews/edits there instead of staring at a preview here.
      if (fields) {
        this._applyOcrFields();
        // _busy is still true here (cleared in finally); defer the step
        // change a tick so finally runs first and the Next/nav buttons
        // aren't left disabled on the destination step.
        setTimeout(() => {
          if (this.state.step === 1) this._goToStep(2);
        }, 0);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('property.wizard.errors.ocr_failed');
      this.setState({ titleDeedLoading: false, titleDeedError: msg });
    } finally {
      this._busy = false;
    }
  }

  // Build a full /units PUT body from a persisted unit row, overlaid with
  // `patch`. The unit update replaces EVERY column, so any step that PUTs
  // the unit (rental, energy, felszereltség, basics re-entry) must send the
  // whole row or it would blank the fields it omits — e.g. the rental step
  // would otherwise wipe the address/energy the earlier steps set. Each
  // step passes only the keys it edits in `patch`; the rest carry through.
  _unitBodyFromRow(row, patch = {}) {
    const base = {
      type: row.type || 'apartment',
      identifier: row.identifier || '1',
      size_sqm: row.size_sqm ?? null,
      room_count: row.room_count ?? null,
      floor: row.floor ?? null,
      helyrajzi_szam: row.helyrajzi_szam ?? null,
      addr_street: row.addr_street ?? null,
      addr_house_number: row.addr_house_number ?? null,
      addr_building: row.addr_building ?? null,
      addr_staircase: row.addr_staircase ?? null,
      addr_door: row.addr_door ?? null,
      postal_code: row.postal_code ?? null,
      city: row.city ?? null,
      country: row.country ?? null,
      asking_rent: row.asking_rent ?? null,
      kozos_koltseg: row.kozos_koltseg ?? null,
      furnished: row.furnished ?? null,
      max_occupants: row.max_occupants ?? null,
      energy_rating: row.energy_rating ?? null,
      energy_certificate_number: row.energy_certificate_number ?? null,
      energy_certificate_expires_at: row.energy_certificate_expires_at ?? null,
      // amenities is JSON in the row but an array in the API payload — decode
      // so a re-entry PUT (e.g. the rental step after the user already saved
      // amenities on the Felszereltség step) carries them through instead of
      // wiping back to NULL.
      amenities: decodeAmenities(row.amenities),
      notes: row.notes ?? null,
      is_main: (row.is_main ?? 1) ? true : false,
      is_published: row.is_published ?? 1,
    };
    return { ...base, ...patch };
  }

  // Overlay only the non-empty address/identity/type fields from the basics
  // form onto the saved unit row (used on the basics re-entry PUT).
  _mergeUnitAddress(row, next) {
    const patch = {};
    if (next.type) patch.type = next.type;
    if (next.identifier) patch.identifier = next.identifier;
    for (const k of ['floor', 'helyrajzi_szam', 'addr_street', 'addr_house_number',
      'addr_building', 'addr_staircase', 'addr_door', 'postal_code', 'city', 'country']) {
      if (next[k] != null && next[k] !== '') patch[k] = next[k];
    }
    patch.is_main = true;
    return this._unitBodyFromRow(row, patch);
  }

  _applyOcrFields() {
    const f = this.state.ocrFields || {};
    if (f.document_type && f.document_type !== 'title_deed') return;
    // Everything from the deed flows onto the FIRST UNIT — it owns the
    // canonical address now; the property has none of its own. Only fill
    // fields the user hasn't already typed so re-applying never tramples
    // manual edits.
    const u = { ...this.state.unit };
    const str = (v) => (v == null ? '' : String(v).trim());
    const street = str(f.street);
    const houseNumber = str(f.house_number);
    if (street && !u.addr_street) u.addr_street = street;
    if (houseNumber && !u.addr_house_number) u.addr_house_number = houseNumber;
    if (str(f.building) && !u.addr_building) u.addr_building = str(f.building);
    if (str(f.staircase) && !u.addr_staircase) u.addr_staircase = str(f.staircase);
    if (str(f.floor) && !u.floor) u.floor = str(f.floor);
    if (str(f.door) && !u.addr_door) u.addr_door = str(f.door);
    if (str(f.postal_code) && !u.postal_code) u.postal_code = str(f.postal_code);
    if (str(f.city) && !u.city) u.city = str(f.city);
    if (str(f.country) && !u.country) u.country = str(f.country).toUpperCase().slice(0, 2);
    const hrsz = f.helyrajzi_szam || f.parcel_number;
    if (hrsz && !u.helyrajzi_szam) u.helyrajzi_szam = String(hrsz);
    if (f.area_sqm != null && f.area_sqm !== '' && !u.size_sqm) u.size_sqm = String(f.area_sqm);
    // Unit type from the deed's ownership_form (lakás → apartment, garázs →
    // garage, tároló → storage, …). Only override the untouched default.
    const mappedType = ownershipFormToUnitType(f.ownership_form);
    if (mappedType && (!u.type || u.type === 'apartment')) u.type = mappedType;
    // Friendly identifier from floor + door ("6/223"), since landlords
    // think of the unit by where it is, not the placeholder "1".
    if (!u.identifier || u.identifier === '1') {
      const floor = str(f.floor);
      const door = str(f.door);
      const ident = (floor && door) ? `${floor}/${door}` : (door || floor || '');
      if (ident) u.identifier = ident;
    }
    this.setState({ unit: u, ocrApplied: true });
  }

  // ---- Step 2: Basics ----

  async _submitBasics() {
    if (this._busy) return;
    clearFormError(this.el);
    const b = this.state.basics;
    // The address now lives entirely on the first unit (see payload.unit
    // below); the property row only caches it, derived server-side from the
    // primary unit. So the property payload here carries just the
    // property-level settings — no address fields.
    const payload = {
      currency: (b.currency || 'HUF').trim().toUpperCase(),
    };
    if (b.default_deposit_months !== '' && b.default_deposit_months !== null && b.default_deposit_months !== undefined) {
      payload.default_deposit_months = Number(b.default_deposit_months);
    }
    if (b.energy_rating && String(b.energy_rating).trim() !== '') {
      payload.energy_rating = String(b.energy_rating).trim().toUpperCase();
    }
    if (b.energy_certificate_number && String(b.energy_certificate_number).trim() !== '') {
      payload.energy_certificate_number = String(b.energy_certificate_number).trim();
    }
    if (b.energy_certificate_expires_at && String(b.energy_certificate_expires_at).trim() !== '') {
      payload.energy_certificate_expires_at = String(b.energy_certificate_expires_at).trim();
    }
    // First-unit basics ride inside payload.unit; the backend uses these
    // to seed the property's primary unit (PropertyService::create).
    const u = this.state.unit || {};
    const unitPayload = {};
    if ((u.identifier || '').trim() !== '') unitPayload.identifier = String(u.identifier).trim();
    if ((u.type || '').trim() !== '')       unitPayload.type       = String(u.type).trim();
    if ((u.floor || '').toString().trim() !== '') unitPayload.floor = String(u.floor).trim();
    if ((u.helyrajzi_szam || '').toString().trim() !== '') unitPayload.helyrajzi_szam = String(u.helyrajzi_szam).trim();
    // The unit's full structured address — this is the canonical address
    // for the whole property; the property row only caches it server-side.
    for (const k of ['addr_street', 'addr_house_number', 'addr_building', 'addr_staircase', 'addr_door', 'postal_code', 'city', 'country']) {
      const v = (u[k] || '').toString().trim();
      if (v !== '') unitPayload[k] = v;
    }
    if (Object.keys(unitPayload).length > 0) payload.unit = unitPayload;

    this._busy = true;
    try {
      let property;
      const wasPreCreate = !this.state.propertyId;
      if (this.state.propertyId) {
        property = await api.put(`/properties/${this.state.propertyId}`, payload);
      } else {
        property = await api.post('/properties', payload);
      }
      // Promote the pre-create draft to the freshly-created property id so a
      // refresh after this point lands on the entity-scoped draft row.
      if (wasPreCreate && property && property.id) {
        attachDraftEntity(KIND, property.id).catch(() => {});
      }
      // Fetch the seeded unit so the Meters step has its id to mount the
      // panel against. Non-fatal — the Meters step shows a loading state
      // if this fails and the user can still advance to Done.
      let unitSaved = null;
      try {
        const list = await api.get(`/properties/${property.id}/units`);
        const seeded = Array.isArray(list?.data) ? list.data : (Array.isArray(list) ? list : []);
        unitSaved = seeded[0] || null;
      } catch (_) { /* non-fatal */ }
      // On a re-entry (PUT) the property already had its unit seeded on the
      // first pass, so create() didn't apply the (possibly edited) address.
      // Push the address + identity onto the primary unit here, merged over
      // its persisted row so rental terms set on the Bérlet step survive.
      // The backend re-derives the property's cached address from this unit.
      if (!wasPreCreate && unitSaved && payload.unit) {
        try {
          const merged = this._mergeUnitAddress(unitSaved, payload.unit);
          const updated = await api.put(`/units/${unitSaved.id}`, merged);
          if (updated && updated.id) unitSaved = updated;
        } catch (_) { /* non-fatal — address stays as previously stored */ }
      }
      // Best-effort: if the user uploaded a title deed on this step,
      // attach it to the seeded unit. We don't auto-create extra units
      // from multi-unit deeds anymore — the user lands on the property
      // page after Done and adds the rest from there.
      const files = Array.isArray(this.state.titleDeedFiles) ? this.state.titleDeedFiles : [];
      const ocr = this.state.ocrFields || null;
      if (unitSaved && files.length > 0 && !this._titleDeedUploaded) {
        const total = files.length;
        let firstDocId = null;
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          const stagedId = this._titleDeedStagedIds[i] || (f && f._stagedId) || null;
          const label = total > 1 ? `${i + 1}/${total}` : null;
          try {
            let doc;
            if (stagedId) {
              const body = { wizard_file_id: stagedId };
              if (label) body.label = label;
              if (ocr && typeof ocr === 'object') body.extracted_fields = ocr;
              doc = await api.post(`/units/${unitSaved.id}/documents/from-wizard-file`, body);
            } else {
              const fd = new FormData();
              fd.append('file', f, f.name || 'title-deed');
              if (label) fd.append('label', label);
              if (ocr && typeof ocr === 'object') fd.append('extracted_fields', JSON.stringify(ocr));
              doc = await api.post(`/units/${unitSaved.id}/documents`, fd);
            }
            if (firstDocId === null && doc && doc.id) firstDocId = doc.id;
          } catch {}
        }
        this._titleDeedUploaded = true;
        // Pre-populate external owners from the first deed page so the
        // Owners step lands on a populated list instead of an empty
        // panel — the user reviews/edits there.
        if (firstDocId) {
          try {
            await api.post(`/units/${unitSaved.id}/external-owners/from-document/${firstDocId}`, {});
          } catch {}
        }
      }
      this._busy = false;
      const nextStep = this.state.step + 1;
      this.setState({
        propertyId: property.id,
        property,
        step: nextStep,
        unitSaved,
      });
      this._syncUrl(nextStep);
    } catch (err) {
      this._busy = false;
      const msg = err instanceof ApiError ? err.message : t('property.wizard.errors.basics_save');
      const field = err instanceof ApiError
        ? fieldFromApiError(err, ['address_line1', 'postal_code', 'city', 'country', 'helyrajzi_szam', 'currency', 'default_deposit_months', 'energy_rating', 'energy_certificate_number', 'energy_certificate_expires_at'])
        : null;
      showFormError(this.el, msg, field ? { field } : {});
    }
  }


}
